import { useState } from 'react';

import { Box, Typography } from '@mui/material';
import {
  MRT_ColumnDef,
  MRT_ColumnOrderState,
  MRT_VisibilityState,
} from 'material-react-table';
import { Operation } from 'src/types/operation';
import { IndicatorsResponse } from 'src/types/wallet';

import { FicFidcCockpitHeader } from './FicFidcCockpitHeader';
import { FicFidcPortfolioConcentrationSection } from './FicFidcPortfolioConcentrationSection';
import { FicFidcAssetConcentrationChart } from './charts/FicFidcAssetConcentrationChart';
import { FicFidcFundConcentrationChart } from './charts/FicFidcFundConcentrationChart';
import { FicFidcManagerConcentrationChart } from './charts/FicFidcManagerConcentrationChart';
import { FicFidcAlertsCard } from './totalizers/FicFidcAlertsTotalizer';
import { FicFidcEquityCard } from './totalizers/FicFidcEquityTotalizer';
import { FicFidcFundsCard } from './totalizers/FicFidcFundsTotalizer';
import { useFicFidcTotalizers } from './useFicFidcTotalizers';
import { useFollowingViewContext } from '../../Wallet/FollowingView/FollowingViewContext';
import { DynamicIndicatorsTable } from '../../Wallet/components/FundIndicatorsTable';
import { IndicatorDetailsModal } from '../../Wallet/components/IndicatorDetailsModal';
import TabNavigation from '../TabNavigation';

interface FicFidcCockpitProps {
  childOperationIds?: string[];
  walletId?: number;
  competencyDate?: string;
  indicatorsResponseData?: IndicatorsResponse[];
  columnsData?: { data: MRT_ColumnDef<any>[] };
  isLoadingIndicators?: boolean;
  isLoadingColumns?: boolean;
  isErrorIndicators?: boolean;
  isEmptyIndicators?: boolean;
}

export function formatParentEquity(
  value?: number | null,
  alternateValue?: number | null,
) {
  const parentValue = value ?? alternateValue;
  if (parentValue == null) return '—';

  if (Math.abs(parentValue) >= 1_000_000) {
    return `R$ ${(parentValue / 1_000_000).toLocaleString('pt-BR', {
      maximumFractionDigits: 1,
    })}M`;
  }

  return `R$ ${parentValue.toLocaleString('pt-BR')}`;
}

const ERROR_REASONS = new Set(['error', 'dependency-error']);

export function formatStatus(status?: string, reason?: string) {
  if (status === 'error' || (reason != null && ERROR_REASONS.has(reason))) {
    return 'Não foi possível carregar os dados';
  }

  return 'Dados indisponíveis para a competência selecionada';
}

function formatCardStatus(
  status: string | undefined,
  reason: string | undefined,
  hasValue: boolean,
  availableMessage: string,
) {
  return hasValue ? availableMessage : formatStatus(status, reason);
}

export function FicFidcCockpit({
  childOperationIds = [],
  walletId = 0,
  competencyDate = '',
  indicatorsResponseData = [],
  columnsData = { data: [] },
  isLoadingIndicators = false,
  isLoadingColumns = false,
  isErrorIndicators = false,
  isEmptyIndicators = false,
}: FicFidcCockpitProps) {
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {},
  );
  const [indicatorsValueType, setIndicatorsValueType] = useState<
    'percentage' | 'amount'
  >('percentage');
  const [operation, setOperation] = useState<Operation | null>(null);
  const [indicatorName, setIndicatorName] = useState('');
  const [indicatorColumn, setIndicatorColumn] = useState('');
  const [openIndicatorDataModal, setOpenIndicatorDataModal] = useState(false);

  const handleIndicatorClick = (
    clickedOperation: Operation,
    clickedIndicatorName: string,
    clickedIndicatorColumn: string,
  ) => {
    setOperation(clickedOperation);
    setIndicatorName(clickedIndicatorName);
    setIndicatorColumn(clickedIndicatorColumn);
    setOpenIndicatorDataModal(true);
  };

  const {
    operationId,
    walletId: contextWalletId,
    indicatorsCompetencyDate,
  } = useFollowingViewContext();
  const { data, isLoading, isError } = useFicFidcTotalizers({
    date: indicatorsCompetencyDate,
    operationId,
    walletId: contextWalletId,
    childOperationIds,
  });
  const status = isError ? 'error' : data?.status;
  const isAvailable = !isLoading && !isError && Boolean(data);
  const hasParentEquity =
    isAvailable &&
    data?.coverage.parent !== false &&
    (data?.parentVlPatrimLiq != null || data?.vl_patrim_liq != null);
  const hasFundsCount =
    isAvailable &&
    data?.coverage.monitoredFunds !== false &&
    data?.monitoredFundsCount != null;
  const hasAlertCount =
    isAvailable && data?.coverage.alerts !== false && data?.alertCount != null;
  const monitoredFundsCount =
    hasFundsCount && data?.monitoredFundsCount != null
      ? data.monitoredFundsCount + 1
      : null;
  const monitoredFundsScope =
    hasFundsCount && data?.monitoredFundsCount != null
      ? `${data.monitoredFundsCount} ${
          data.monitoredFundsCount === 1 ? 'FIDC' : 'FIDCs'
        } + 1 FIC`
      : null;

  return (
    <Box
      id="fic-fidc-cockpit"
      component="section"
      aria-label="Cockpit do FIC seguido"
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: 'grey.50',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}>
      <FicFidcCockpitHeader />

      <Box
        id="fic-fidc-cockpit-totalizers"
        aria-label="Totalizadores do cockpit"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
          mb: { xs: 2, md: 3 },
        }}>
        <FicFidcEquityCard
          complement={formatCardStatus(
            status,
            data?.reason,
            hasParentEquity,
            'Valor financeiro na última competência',
          )}
          isLoading={isLoading}
          value={
            hasParentEquity
              ? formatParentEquity(data?.parentVlPatrimLiq, data?.vl_patrim_liq)
              : '—'
          }
        />
        <FicFidcFundsCard
          complement={formatCardStatus(
            status,
            data?.reason,
            hasFundsCount,
            monitoredFundsScope ?? 'Fundos monitorados',
          )}
          isLoading={isLoading}
          value={monitoredFundsCount ?? '—'}
        />
        <FicFidcAlertsCard
          complement={
            hasAlertCount && data.alertCount === 0
              ? 'Sem alertas acionados'
              : hasAlertCount
                ? ''
                : formatStatus(status, data?.reason)
          }
          isLoading={isLoading}
          value={
            hasAlertCount ? (
              <>
                {data.alertCount}{' '}
                <Typography component="span" color="inherit" variant="h6">
                  alertas ativos
                </Typography>
              </>
            ) : (
              '—'
            )
          }
        />
      </Box>

      <FicFidcPortfolioConcentrationSection />

      <Box
        id="fic-fidc-cockpit-charts"
        aria-label="Gráficos resumidos do cockpit"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
          mb: 2,
        }}>
        <FicFidcFundConcentrationChart />
        <FicFidcAssetConcentrationChart />
        <FicFidcManagerConcentrationChart />
      </Box>

      <Box
        id="fic-fidc-cockpit-composition"
        aria-label="Tabela de indicadores FIC/FIDC"
        component="section"
        sx={{
          minHeight: 280,
          minWidth: 0,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
        }}>
        {isErrorIndicators ? (
          <Box p={3} role="alert">
            <Typography color="error">
              Não foi possível carregar os indicadores da composição.
            </Typography>
          </Box>
        ) : isEmptyIndicators && !isLoadingIndicators && !isLoadingColumns ? (
          <Box p={3}>
            <Typography color="text.secondary">
              Nenhum indicador disponível para esta competência.
            </Typography>
          </Box>
        ) : walletId === 0 ? null : (
          <Box display="flex" flexDirection="column" gap={0}>
            <TabNavigation
              value={indicatorsValueType}
              handleSelectTab={setIndicatorsValueType}
            />
            <DynamicIndicatorsTable
              walletId={walletId}
              viewMode="wallet"
              columnOrder={columnOrder}
              setColumnOrder={setColumnOrder}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              isLoadingColumns={isLoadingColumns}
              isLoadingIndicators={isLoadingIndicators}
              indicatorsResponseData={indicatorsResponseData}
              columnsData={columnsData}
              valueType={indicatorsValueType}
              handleIndicatorClick={handleIndicatorClick}
              enableFollowActions
              isFollowedFunds
            />
          </Box>
        )}
      </Box>

      {openIndicatorDataModal && (
        <IndicatorDetailsModal
          open
          onClose={() => setOpenIndicatorDataModal(false)}
          targetDate={competencyDate}
          document={operation?.cnpj || ''}
          indicatorName={indicatorName}
          indicatorColumn={indicatorColumn}
        />
      )}

      <Box
        id="fic-fidc-cockpit-footer"
        component="footer"
        aria-label="Rodapé informativo do cockpit"
        sx={{ height: 24, flexShrink: 0 }}
      />
    </Box>
  );
}

export default FicFidcCockpit;
